// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Leaderboard, LeaderboardRecord, LeaderboardRecordSearchFilters, LeaderboardSortOrder, LeaderboardOperator } from '../../types/leaderboards';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './LeaderboardDetail.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const PageFirstIcon = () => (
  <img src="/static/svg/page-first.svg" alt="First page" width="20" height="20" />
);

const PageNextIcon = () => (
  <img src="/static/svg/page-next.svg" alt="Next page" width="20" height="20" />
);

const PagePrevIcon = () => (
  <img src="/static/svg/page-prev.svg" alt="Previous page" width="20" height="20" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const LeaderboardDetail: React.FC = () => {
  const { leaderboardId } = useParams<{ leaderboardId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [leaderboard, setLeaderboard] = useState<Leaderboard | null>(null);
  const [records, setRecords] = useState<LeaderboardRecord[]>([]);
  const [nextCursor, setNextCursor] = useState('');
  const [prevCursor, setPrevCursor] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState<LeaderboardRecordSearchFilters>({
    owner_ids: [],
    limit: 50,
  });
  const [resetting, setResetting] = useState(false);

  const sessionRole = authService.getSessionRole();

  useEffect(() => {
    if (leaderboardId) {
      loadLeaderboard();
      
      // Initialize filters from URL params
      const cursor = searchParams.get('cursor') || '';
      const limit = searchParams.get('limit');
      const ownerIds = searchParams.getAll('owner_ids');

      setSearchFilters({
        owner_ids: ownerIds,
        limit: limit ? parseInt(limit) : 50,
        cursor: cursor,
      });

      loadRecords(cursor, {
        owner_ids: ownerIds,
        limit: limit ? parseInt(limit) : 50,
      });
    }
  }, [leaderboardId]);

  const loadLeaderboard = useCallback(async () => {
    if (!leaderboardId) return;

    setLoading(true);
    setError('');

    try {
      const result = await consoleService.getLeaderboard('', leaderboardId);
      setLeaderboard(result);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [leaderboardId]);

  const loadRecords = useCallback(async (
    cursor?: string,
    filters?: LeaderboardRecordSearchFilters
  ) => {
    if (!leaderboardId) return;

    setRecordsLoading(true);
    setError('');

    try {
      const result = await consoleService.listLeaderboardRecords(
        '',
        leaderboardId,
        { ...filters, cursor }
      );

      setRecords(result.records || []);
      setNextCursor(result.next_cursor || '');
      setPrevCursor(result.prev_cursor || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load leaderboard records');
    } finally {
      setRecordsLoading(false);
    }
  }, [leaderboardId]);

  const handleSearch = useCallback((direction: number = 0) => {
    let cursor = '';
    switch (direction) {
      case -1:
        cursor = prevCursor;
        break;
      case 0:
        cursor = '';
        break;
      case 1:
        cursor = nextCursor;
        break;
    }

    // Update URL params
    const params = new URLSearchParams();
    if (searchFilters.owner_ids && searchFilters.owner_ids.length > 0) {
      searchFilters.owner_ids.forEach(id => params.append('owner_ids', id));
    }
    if (searchFilters.limit) params.set('limit', searchFilters.limit.toString());
    if (cursor) params.set('cursor', cursor);
    
    setSearchParams(params);
    loadRecords(cursor, searchFilters);
  }, [searchFilters, nextCursor, prevCursor, setSearchParams, loadRecords]);

  const handleFilterChange = (field: keyof LeaderboardRecordSearchFilters, value: any) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(0);
  };

  const handleDeleteRecord = (record: LeaderboardRecord, index: number) => {
    openConfirmDialog(
      'Delete Record',
      `Are you sure you want to delete the record for user "${record.username || record.owner_id}"? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteLeaderboardRecord('', leaderboardId!, record.owner_id);
          setRecords(prev => prev.filter((_, i) => i !== index));
          setError('');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete record');
        }
      }
    );
  };

  const handleResetLeaderboard = () => {
    openConfirmDialog(
      'Reset Leaderboard',
      `Are you sure you want to reset the leaderboard "${leaderboard?.id}"? This will delete all records and cannot be undone.`,
      async () => {
        setResetting(true);
        setError('');
        try {
          await consoleService.leaderboardReset(leaderboardId!);
          // Reload leaderboard and records
          await loadLeaderboard();
          const cursor = searchParams.get('cursor') || '';
          await loadRecords(cursor, searchFilters);
          setError('Leaderboard reset successfully!');
          setTimeout(() => setError(''), 3000);
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to reset leaderboard');
        } finally {
          setResetting(false);
        }
      }
    );
  };

  const handleBackToLeaderboards = () => {
    navigate('/leaderboards');
  };

  const deleteAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getSortOrderText = (sortOrder: number) => {
    return sortOrder === LeaderboardSortOrder.ASCENDING ? 'Ascending' : 'Descending';
  };

  const getOperatorText = (operator: number) => {
    switch (operator) {
      case LeaderboardOperator.BEST: return 'Best';
      case LeaderboardOperator.SET: return 'Set';
      case LeaderboardOperator.INCREMENT: return 'Increment';
      case LeaderboardOperator.DECREMENT: return 'Decrement';
      default: return 'No Op';
    }
  };

  const formatMetadata = (metadata?: string) => {
    if (!metadata) return '';
    try {
      const parsed = JSON.parse(metadata);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return metadata;
    }
  };

  const formatScore = (score: string, numScore: number) => {
    return `${score} (${numScore})`;
  };

  if (loading) {
    return (
      <div className="leaderboard-detail-container">
        <div className="alert alert-info">
          <LoadingSpinner />
          <strong className="ms-2">Loading leaderboard...</strong>
        </div>
      </div>
    );
  }

  if (!leaderboard) {
    return (
      <div className="leaderboard-detail-container">
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Leaderboard not found</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-detail-container">
      <div className="leaderboard-detail-header">
        <div className="header-actions">
          <button
            type="button"
            className="btn btn-outline-secondary back-btn"
            onClick={handleBackToLeaderboards}
          >
            ← Back to Leaderboards
          </button>
          {deleteAllowed() && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleResetLeaderboard}
              disabled={resetting}
            >
              {resetting ? 'Resetting...' : '🔄 Reset Leaderboard'}
            </button>
          )}
        </div>
        <h2 className="leaderboard-title">{leaderboard.id}</h2>
        {leaderboard.title && (
          <h6 className="leaderboard-subtitle">{leaderboard.title}</h6>
        )}
      </div>

      <div className="leaderboard-info-section">
        <div className="info-grid">
          <div className="info-item">
            <label>Type:</label>
            <span className={`type-badge ${leaderboard.authoritative ? 'authoritative' : 'client'}`}>
              {leaderboard.authoritative ? 'Authoritative' : 'Client'}
            </span>
          </div>
          <div className="info-item">
            <label>Sort Order:</label>
            <span>{getSortOrderText(leaderboard.sort_order)}</span>
          </div>
          <div className="info-item">
            <label>Operator:</label>
            <span>{getOperatorText(leaderboard.operator)}</span>
          </div>
          <div className="info-item">
            <label>Category:</label>
            <span>{leaderboard.category !== undefined ? leaderboard.category : '-'}</span>
          </div>
          <div className="info-item">
            <label>Created:</label>
            <span>{formatDate(leaderboard.create_time)}</span>
          </div>
        </div>

        {leaderboard.description && (
          <div className="info-item full-width">
            <label>Description:</label>
            <p>{leaderboard.description}</p>
          </div>
        )}

        {leaderboard.metadata && (
          <div className="info-item full-width">
            <label>Metadata:</label>
            <pre className="metadata-content">{formatMetadata(leaderboard.metadata)}</pre>
          </div>
        )}
      </div>

      <div className="records-section">
        <div className="records-header">
          <h4>Leaderboard Records</h4>
          <h6 className="records-subtitle">{records.length} records found.</h6>
        </div>

        <div className="records-controls">
          <div className="search-section">
            <form onSubmit={handleSubmit} className="search-form">
              <div className="search-input-group">
                <input
                  type="text"
                  className="search-input"
                  value={searchFilters.owner_ids?.join(',') || ''}
                  onChange={(e) => handleFilterChange('owner_ids', e.target.value.split(',').filter(id => id.trim()))}
                  placeholder="Filter by owner IDs (comma-separated)"
                />
                <select
                  className="limit-select"
                  value={searchFilters.limit || 50}
                  onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                >
                  <option value={10}>10 records</option>
                  <option value={25}>25 records</option>
                  <option value={50}>50 records</option>
                  <option value={100}>100 records</option>
                </select>
                <div className="search-buttons">
                  <button
                    type="submit"
                    className="btn btn-primary search-btn"
                  >
                    Search
                  </button>
                </div>
              </div>
            </form>
          </div>

          <div className="pagination-section">
            <div className="pagination-buttons">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => handleSearch(0)}
                disabled={records.length === 0 || recordsLoading}
                title="First page"
              >
                <PageFirstIcon />
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => handleSearch(-1)}
                disabled={!prevCursor || recordsLoading}
                title="Previous page"
              >
                <PagePrevIcon />
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => handleSearch(1)}
                disabled={!nextCursor || recordsLoading}
                title="Next page"
              >
                <PageNextIcon />
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <RedTriangleIcon />
            <strong>Error:</strong> {error}
          </div>
        )}

        {recordsLoading && (
          <div className="alert alert-info">
            <LoadingSpinner />
            <strong className="ms-2">Loading records...</strong>
          </div>
        )}

        <div className="records-table-container">
          <table className="records-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Owner ID</th>
                <th>Username</th>
                <th>Score</th>
                <th>Subscore</th>
                <th>Updated</th>
                {deleteAllowed() && <th style={{ width: '120px' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && !recordsLoading && (
                <tr>
                  <td colSpan={deleteAllowed() ? 7 : 6} className="no-records">
                    No records found for this leaderboard.
                  </td>
                </tr>
              )}
              {records.map((record, index) => (
                <tr key={`${record.owner_id}-${record.leaderboard_id}`} className="record-row">
                  <td className="rank-cell">
                    <span className="rank-badge">
                      {record.rank || (index + 1)}
                    </span>
                  </td>
                  <td>
                    <code className="owner-id">{record.owner_id}</code>
                  </td>
                  <td>
                    {record.username || '-'}
                  </td>
                  <td>
                    <div className="score-display">
                      <span className="score">{record.score}</span>
                      <span className="num-score">({record.num_score})</span>
                    </div>
                  </td>
                  <td>
                    {record.subscore || '-'}
                  </td>
                  <td>
                    {formatDate(record.update_time)}
                  </td>
                  {deleteAllowed() && (
                    <td className="actions-cell">
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteRecord(record, index)}
                        title="Delete record"
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default LeaderboardDetail;
